import type { StatCell, Unit } from "@sins2-atlas/game-data";
import {
  BASE_FRAME,
  bareHull,
  deselectAllTech,
  type StatFrame,
  selectAllTech,
  serializeFrame,
} from "./frame";
import { frameKey } from "./frame-fold";
import { reconcileFrame } from "./frame-reconcile";

// The compare-set URL contract (ADR 0002 + Issue 07). The set is a comma-separated list
// of stable game unit ids, ORDER-PRESERVING — column order is part of the shared matchup,
// so unlike the facet params it is never canonicalized. It rides along as ?compare= on
// /ships, /structures and /unit (following every unit-level link, the frame precedent) and
// renders at /compare?units=… — the prototype's own shareable URL form. Malformed tokens are link
// damage and drop; well-formed ids the dataset does not know SURVIVE parsing so the
// compare view can name them instead of silently changing a shared matchup's meaning.

/** The design prototype's own add-cap: four units compare legibly on desktop; the
 * affordances disable beyond it and the view explains an overlong hand-edited link. */
export const COMPARE_LIMIT = 4;

// The dataset's unit-id shape — anything else in the param cannot be a unit.
const ID_SHAPE = /^[a-z0-9_]+$/;

/** Parse the CSV param: order preserved, duplicates collapse to their first occurrence,
 * malformed tokens drop. Deliberately uncapped — policy (the cap, the unknown-id notice)
 * lives at the use sites, which know what to tell the user. */
export function parseCompareParam(value: string | undefined): string[] {
  if (value === undefined || value === "") return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const token of value.split(",")) {
    if (!ID_SHAPE.test(token) || seen.has(token)) continue;
    seen.add(token);
    ids.push(token);
  }
  return ids;
}

// A column is a unit under its OWN stat frame (ADR 0011), not a bare id: the same hull may
// stand twice under two Tech Sets or two Corruptions, which is the question a tech comparison
// usually is. So the set is deduplicated on the PAIR, and the cap of four counts pairs.

export interface CompareColumn {
  id: string;
  frame: StatFrame;
  /** Which place in the `?units=` list this column was parsed from — hence which frame slot
   * it reads. Dropped tokens keep their place, so it is not the column's position on screen.
   * It describes the link the column came from, so it is meaningless on a list the page has
   * since edited; only `compareMatchup` reads it, and only on what it just parsed. */
  slot: number;
}

/** A column's identity — what deduplication and React keys are both about. The frame's half
 * is frameKey's, so two columns are the same column exactly when they would fold identically
 * (lib/frame-fold.ts owns that judgement, and the memoised fold already agrees with it). */
export function columnKey(column: CompareColumn): string {
  return `${column.id}|${frameKey(column.frame)}`;
}

/** The matchup: the `?units=` tokens paired with the frame slots, in column order.
 *
 * Slots align with the units param by POSITION IN THE LINK, not by surviving column — a
 * malformed token takes its own slot with it rather than sliding every later column's frame
 * one place left. Everything the id-only set did survives: order is never canonicalized,
 * malformed tokens drop (and `malformedCompareTokens` names them), well-formed ids the dataset
 * does not know are kept for /compare to explain, and the list is deliberately uncapped so the
 * view can count the overflow. Only the dedupe key moved. */
export function parseCompareColumns(
  value: string | undefined,
  frames: readonly StatFrame[],
): CompareColumn[] {
  if (value === undefined || value === "") return [];
  const seen = new Set<string>();
  const columns: CompareColumn[] = [];
  value.split(",").forEach((token, index) => {
    if (!ID_SHAPE.test(token)) return;
    const column: CompareColumn = { id: token, frame: frameAt(frames, index), slot: index };
    const key = columnKey(column);
    if (seen.has(key)) return;
    seen.add(key);
    columns.push(column);
  });
  return columns;
}

/** How many well-formed tokens the pair dedupe dropped: the same unit named twice under the
 * same stats. Two such columns would print the same numbers twice, so ADR 0011 keeps one —
 * but a link that asked for both, or a bulk control that has just made two columns identical,
 * loses a column, and this view reports what it drops (ADR 0002 amendment). Not the same as
 * `malformedCompareTokens`, which is about damage: these tokens are perfectly good. */
export function mergedColumns(
  value: string | undefined,
  requested: readonly CompareColumn[],
): number {
  if (value === undefined || value === "") return 0;
  const wellFormed = value.split(",").filter((token) => ID_SHAPE.test(token)).length;
  return wellFormed - requested.length;
}

/** The matchup with one column dropped, addressed by its pair KEY rather than its position.
 * A well-formed id the dataset does not know keeps its place in the set but renders no column,
 * so the Nth Remove button is not the Nth pair; removing by position dropped the unknown id and
 * left the unit the reader clicked standing. The key is unique by construction — the set is
 * deduplicated on it. */
export function removeColumn(columns: readonly CompareColumn[], key: string): CompareColumn[] {
  return columns.filter((column) => columnKey(column) !== key);
}

/** One column's own controls writing its own slot (Issue 41): the frame at `index` becomes
 * `frame` and every other column is handed back untouched. Addressed by POSITION here rather
 * than by pair key, because the key is a function of the very frame that is changing. */
export function setColumnFrame(
  columns: readonly CompareColumn[],
  index: number,
  frame: StatFrame,
): CompareColumn[] {
  return columns.map((column, at) => (at === index ? { ...column, frame } : column));
}

/** A bulk control's whole move: one axis set on every column (bulkAxis), then every column
 * refitted against its OWN hull exactly as the entity page refits one (reconcileFrame) —
 * a research change can invalidate a Loadout, and whatever came off is said out loud, named
 * by its hull, because a Loadout that quietly loses an item is a Loadout the reader cannot
 * trust. Neither bulk axis moves a Level, so the allocation half of that reconcile never
 * trims here; it is the same door all the same, so a new bulk axis cannot skip it. A column
 * whose id the dataset does not know takes the axis and nothing else.
 *
 * The frames handed in must be the ones the columns SHOW — settled, not the link's raw
 * slots. A cold `?items=` slot has never been through a tick, so the page settles it before
 * rendering; a bulk click that read the raw slot instead ran the refit over a frame with no
 * research and took off the very item the column had just shown equipped (review
 * 2026-09-08). */
export function applyBulkAxis(
  columns: readonly { frame: StatFrame; unit?: Unit }[],
  next: StatFrame,
  axis: FrameAxis,
): { frames: StatFrame[]; notes: string[] } {
  const notes: string[] = [];
  const frames = bulkAxis(
    columns.map((column) => column.frame),
    next,
    axis,
  ).map((frame, index) => {
    const unit = columns[index]?.unit;
    if (unit === undefined) return frame;
    const change = reconcileFrame(unit, columns[index]?.frame ?? frame, frame);
    if (change.note !== undefined) notes.push(`${unit.name}: ${change.note}`);
    return change.frame;
  });
  return { frames, notes };
}

/** Adding a hull the matchup already holds (Issue 41): the same hull may stand up to four
 * times under four frames (ADR 0011), so an add is never swallowed by the id — it is a new
 * column. The copy starts from the BULK frame, the one the page's own control names.
 *
 * When that column already stands, the copy takes the other end of the research axis
 * instead: two columns of one hull under one frame show the same numbers twice, so the pair
 * dedupe would swallow it, and Base beside Fully-Teched is the comparison the reader asking
 * for a second copy is asking for. `undefined` when the matchup is full, or when the hull
 * already stands at both extents and there is nothing new to draw — the affordance disables
 * itself and says so. */
export function addColumnAgain(
  columns: readonly CompareColumn[],
  id: string,
  bulk: StatFrame,
): CompareColumn[] | undefined {
  if (columns.length >= COMPARE_LIMIT) return undefined;
  const standing = new Set(columns.map(columnKey));
  // The seed is bare of the two per-hull levers (Issue 52, ADR 0012): a Loadout and a Level
  // belong to one hull in one place, and the bulk frame is column one's WHOLE frame — so
  // without this a second Kol would arrive wearing the first one's items and its Level. It is
  // also what makes Add again on a levelled column draw the level-1 twin the reader wants,
  // rather than a duplicate the dedupe pushes onto the other end of the research axis.
  const seed = bareHull(bulk);
  for (const frame of [seed, deselectAllTech(seed), selectAllTech(seed)]) {
    const column: CompareColumn = { id, frame, slot: columns.length };
    if (!standing.has(columnKey(column))) return [...columns, column];
  }
  return undefined;
}

/** Why a second column of this hull cannot be drawn, or undefined when it can — the one
 * sentence the affordance says when it refuses, so the button and its title cannot drift. */
export function addAgainRefusal(
  columns: readonly CompareColumn[],
  id: string,
  bulk: StatFrame,
): string | undefined {
  if (addColumnAgain(columns, id, bulk) !== undefined) return undefined;
  if (columns.length >= COMPARE_LIMIT) return `The comparison is full (${COMPARE_LIMIT} units)`;
  // Names every lever a column carries (Issue 52), not only the two it carried before: a
  // reader whose matchup is full of one hull's research extents can still split it by Level or
  // by what it has equipped, and the refusal must not send them looking for the wrong control.
  return "This unit already stands under both base and fully-teched stats. Change a column's Tech, Corruption, Level or ship items first.";
}

export function serializeCompareParam(ids: readonly string[]): string | undefined {
  return ids.length === 0 ? undefined : ids.join(",");
}

/** The pending set as the UI owns it: at most COMPARE_LIMIT ids, first-listed first. */
export function clampCompare<T>(members: readonly T[]): T[] {
  return members.slice(0, COMPARE_LIMIT);
}

/** Tokens in the param that cannot be a unit id at all — link damage (spaces, capitals,
 * punctuation) that `parseCompareParam` drops, reported so a mangled share does not lose a
 * column in silence (ADR 0002 amendment 2026-07-30).
 *
 * Deliberately NOT reported: empty tokens, which name nothing, and duplicates, which lose
 * nothing because the unit they name is still in the set. A well-formed id the dataset does
 * not know is not damage either — it survives parsing, and /compare names it against the
 * dataset, which is the only place that knows whether an id is real. */
export function malformedCompareTokens(value: string | undefined): string[] {
  if (value === undefined || value === "") return [];
  return [...new Set(value.split(","))].filter((token) => token !== "" && !ID_SHAPE.test(token));
}

/** How many ids past the cap the set dropped — 0 when the link fits. */
export function compareOverflow(members: readonly unknown[]): number {
  return Math.max(0, members.length - COMPARE_LIMIT);
}

/** Toggle one unit: present removes (order preserved), absent appends as the newest
 * column. A full set refuses adds — callers disable the affordance and say why. */
export function toggleCompareId(current: readonly string[], id: string): string[] {
  if (current.includes(id)) return current.filter((existing) => existing !== id);
  if (current.length >= COMPARE_LIMIT) return [...current];
  return [...current, id];
}

/** The state and the action behind a compare affordance. Two of them exist — the roster
 * row's tick and the entity page's button — and they had each derived `inSet`, derived
 * `full`, re-guarded the commit and re-typed the same refusal sentence. Their MARKUP
 * genuinely differs (a 12px checkbox versus a labelled button), so only the policy is
 * shared; each site still writes its own add/remove wording, because the tick has no
 * visible label and the button does. */
export interface CompareToggle {
  /** Whether the unit is already a column in the pending set. */
  inSet: boolean;
  /** Whether an add is refused because the set is at COMPARE_LIMIT. */
  full: boolean;
  /** The one sentence both affordances say when they refuse: the cap is a single rule. */
  fullTitle: string;
  /** Apply the toggle. A full set is a no-op, so the guard cannot be forgotten at a call
   * site — the affordance still disables itself, this just makes it true either way. */
  commit: () => void;
}

export function compareToggle(
  current: readonly string[],
  id: string,
  onChange: (next: string[]) => void,
): CompareToggle {
  const inSet = current.includes(id);
  const full = !inSet && current.length >= COMPARE_LIMIT;
  return {
    inSet,
    full,
    fullTitle: `The comparison is full (${COMPARE_LIMIT} units)`,
    commit: () => {
      if (!full) onChange(toggleCompareId(current, id));
    },
  };
}

/** Which way a compare row's best value points. The prototype's rule: mark only rows
 * whose direction is the stat's own purpose (performance and efficiency rows); cost and
 * fleet facts carry no mark — "cheapest" across a titan and a frigate says nothing. */
export type BestDirection = "higher" | "lower";

/** The row's best value, or undefined when fewer than two cells are Computed — a best
 * against nothing is noise, and non-Computed states never compete. Ties share the value,
 * so every tied cell gets marked. */
export function bestValue(
  cells: readonly StatCell[],
  direction: BestDirection,
): number | undefined {
  const values = cells.filter((cell) => cell.state === "computed").map((cell) => cell.value);
  if (values.length < 2) return undefined;
  return direction === "higher" ? Math.max(...values) : Math.min(...values);
}

// The column-list operations the comparison alone needs (ADR 0011). They lived in frame.ts
// beside the URL grammar until review 2026-09-08; only routes/compare.tsx reads them.

/** How many slots the URL set that no column reads AND that would have changed something.
 * `readSlots` names the places in the link the kept columns came from, NOT how many columns
 * there are: a dropped token keeps its place, so a slot after one is read by the column that
 * follows it, and counting from the column total reported a Corruption the page is visibly
 * under as changing nothing. A surplus Base slot is not counted either — it says exactly what
 * a missing slot says, the rule an empty `?units=` token already follows. */
export function surplusFrameSlots(
  frames: readonly StatFrame[],
  readSlots: readonly number[],
): number {
  const read = new Set(readSlots);
  const base = frameKey(BASE_FRAME);
  return frames.filter((frame, index) => !read.has(index) && frameKey(frame) !== base).length;
}

/** One axis of the stat frame pair (CONTEXT.md: Stat frames). The two are orthogonal by
 * decision (ADR 0007), so every bulk write names the one it moves. */
export type FrameAxis = "research" | "corruption";

/** Set ONE axis of every column at once — the bulk control's whole job (ADR 0011, PRD story
 * 45: one frame for the whole comparison stays one action). Each bulk control NAMES its axis
 * rather than having it inferred, because a matchup whose columns disagree has no value to
 * infer from: with columns at Base and Fully-Teched the control can only be showing one of
 * them, so "Deselect all" would look like no change at all and the other column would keep
 * its research. Naming the axis also keeps a Corruption click off the research axis, which
 * is what the superseded whole-frame diffing form was built to protect. */
export function bulkAxis(
  frames: readonly StatFrame[],
  next: StatFrame,
  axis: FrameAxis,
): StatFrame[] {
  return frames.map((frame) =>
    axis === "research"
      ? { ...frame, research: next.research, tech: next.tech, techUnknown: next.techUnknown }
      : { ...frame, corruption: next.corruption },
  );
}

/** What the bulk controls show above a matchup whose columns each carry their own frame
 * (Issue 41): the first column's frame to seed the panels from, and per axis whether the
 * columns actually agree on it. A control over a disagreeing axis must not claim one
 * column's value for all of them — that is the very line ADR 0011 retired — so it says
 * "mixed" and stays fully usable, since setting every column at once is exactly the action
 * a mixed axis needs. */
export interface AgreedFrame {
  /** The frame the bulk panels edit from: the first column's, or Base with no columns. */
  frame: StatFrame;
  techMixed: boolean;
  corruptionMixed: boolean;
}

export function agreedFrame(frames: readonly StatFrame[]): AgreedFrame {
  const frame = frameAt(frames, 0);
  const tech = serializeFrame(frame).tech;
  return {
    frame,
    techMixed: frames.some((other) => serializeFrame(other).tech !== tech),
    corruptionMixed: frames.some((other) => other.corruption !== frame.corruption),
  };
}

/** The frame a column reads under: its own slot, or Base when the URL named fewer slots than
 * the matchup has columns. A link that sets no frame at all is a Base matchup, and a link
 * that sets only the first column leaves the rest at Base — the same rule either way. */
export function frameAt(frames: readonly StatFrame[], index: number): StatFrame {
  return frames[index] ?? BASE_FRAME;
}
